import { computed, inject, Injector } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { type ActivatedRouteSnapshot, type CanActivateFn, Router, type UrlTree } from '@angular/router';
import { filter, map, type Observable, take } from 'rxjs';

import { ExperimentationService } from '#core/services/experimentation.service';
import { RestaurantsStore } from '#modules/restaurants/stores/restaurants.store';

// The module is released per organization: the selected one at group level, the restaurant's at restaurant level.
// The restaurant is looked up in the user's list because a guard runs before the parent route resolves it.
export const feedbackFormsFeatureGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
    const router = inject(Router);
    const injector = inject(Injector);
    const experimentationService = inject(ExperimentationService);
    const restaurantsStore = inject(RestaurantsStore);

    const restaurantId = route.paramMap.get('restaurant_id');
    const isExperimentationLoaded = toSignal(experimentationService.isLoaded$, { initialValue: false, injector });
    const organizationId = computed(() =>
        restaurantId
            ? (restaurantsStore.restaurants().find((restaurant) => restaurant.id === restaurantId)?.organization?.id ?? null)
            : restaurantsStore.selectedOrganizationId()
    );
    // The organization is auto-selected in an effect after the restaurants land, so a loaded store does not mean a selected organization yet.
    const isReady = computed(
        () =>
            isExperimentationLoaded() &&
            restaurantsStore.isLoaded() &&
            (organizationId() !== null || !!restaurantId || restaurantsStore.restaurants().length === 0)
    );

    return toObservable(isReady, { injector }).pipe(
        filter(Boolean),
        take(1),
        map(() => {
            const currentOrganizationId = organizationId();
            const isEnabled =
                !!currentOrganizationId &&
                experimentationService.isFeatureEnabledForGivenOrganization('release-nps', currentOrganizationId);
            // A switch from another restaurant or from the group keeps the path, so the target restaurant may not have the module.
            return isEnabled || router.createUrlTree(restaurantId ? ['/restaurants', restaurantId, 'dashboard'] : ['/']);
        })
    );
};
